import { cn } from '@/lib/utils';
import { FileText, Video, Lock } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useServiceAccess } from '@/hooks/useServiceAccess';
import { SERVICE_KEYS } from '@/lib/serviceKeys';

interface DualScoreBadgeProps {
  cvScore?: number | null;
  interviewScore?: number | null;
  size?: 'sm' | 'md';
  className?: string;
}

function getScoreColor(score: number) {
  if (score >= 80) return 'text-success border-success/40 bg-success/5';
  if (score >= 60) return 'text-warning border-warning/40 bg-warning/5';
  return 'text-destructive border-destructive/40 bg-destructive/5';
}

function ScorePill({ score, icon, label, disabled, disabledMessage }: {
  score: number | null | undefined;
  icon: React.ReactNode;
  label: string;
  disabled?: boolean;
  disabledMessage?: string;
}) {
  if (disabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center justify-center gap-1 min-w-[3.5rem] px-2 py-0.5 rounded-full border border-border bg-muted/30 text-muted-foreground text-xs font-medium opacity-50">
            <Lock className="w-3 h-3" />
            <span>—</span>
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs max-w-[200px]">{disabledMessage || `${label}: Service unavailable`}</TooltipContent>
      </Tooltip>
    );
  }

  if (score == null) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center justify-center gap-1 min-w-[3.5rem] px-2 py-0.5 rounded-full border border-border bg-muted/30 text-muted-foreground text-xs font-medium">
            {icon}
            <span>—</span>
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">{label}: Not scored</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn(
          'inline-flex items-center justify-center gap-1 min-w-[3.5rem] px-2 py-0.5 rounded-full border text-xs font-bold tabular-nums',
          getScoreColor(score),
        )}>
          {icon}
          <span>{score}%</span>
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="text-xs">{label}: {score}%</TooltipContent>
    </Tooltip>
  );
}

export function DualScoreBadge({ cvScore, interviewScore, size = 'sm', className }: DualScoreBadgeProps) {
  const iconSize = size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5';
  const { hasAccess: hasCvScoring } = useServiceAccess(SERVICE_KEYS.AI_CV_SCORING);
  const { hasAccess: hasInterviews } = useServiceAccess(SERVICE_KEYS.AI_INTERVIEWS);

  return (
    <div className={cn('flex max-w-full flex-wrap items-center gap-1.5', className)}>
      <ScorePill
        score={cvScore}
        icon={<FileText className={cn(iconSize, 'shrink-0')} />}
        label="CV Score"
        disabled={!hasCvScoring && cvScore == null}
        disabledMessage="AI CV Scoring is unavailable"
      />
      <ScorePill
        score={interviewScore}
        icon={<Video className={cn(iconSize, 'shrink-0')} />}
        label="Interview Score"
        disabled={!hasInterviews && interviewScore == null}
        disabledMessage="AI Interviews is unavailable"
      />
    </div>
  );
}
